/**
 * Generic client-side event buffer that collects events and flushes them
 * in batches via a caller-supplied transport function.
 *
 * Flush triggers:
 * - Timer: every `flushIntervalMs` (default 2 000 ms)
 * - Threshold: when buffer reaches `maxBufferSize` (default 20)
 * - Explicit: call `flush()`
 * - Page unload: via `visibilitychange` / `pagehide` with `sendBeacon` fallback
 *
 * Usage (in app-specific code):
 * ```ts
 * const buffer = new EventBuffer<MyEvent>({
 *     sendBatch: (events) => axios.post('/api/v1/log', events),
 *     sendBeaconBatch: (events) => {
 *         const blob = new Blob([JSON.stringify(events)], { type: 'application/json' })
 *         navigator.sendBeacon('/api/v1/log', blob)
 *     },
 * })
 *
 * buffer.push({ event: 'CLICK', referenceId: '...' })
 * ```
 */

interface EventBufferConfig<T> {
    /** Async transport — used for normal flushes. */
    sendBatch: (events: T[]) => Promise<unknown>
    /** Sync transport — used on page unload (sendBeacon). Optional. */
    sendBeaconBatch?: (events: T[]) => void
    /** Flush interval in ms (default: 2000). */
    flushIntervalMs?: number
    /** Max events before immediate flush (default: 20). */
    maxBufferSize?: number
    /** Error handler (default: console.error). */
    onError?: (error: unknown) => void
}

export class EventBuffer<T> {
    private buffer: T[] = []
    private timer: ReturnType<typeof setTimeout> | null = null
    private readonly config: Required<Pick<EventBufferConfig<T>, 'flushIntervalMs' | 'maxBufferSize'>> &
        EventBufferConfig<T>

    constructor(config: EventBufferConfig<T>) {
        this.config = {
            flushIntervalMs: 2_000,
            maxBufferSize: 20,
            ...config,
        }

        if (typeof window !== 'undefined') {
            this.registerUnloadHandlers()
        }
    }

    /** Add an event to the buffer. Triggers a flush if the threshold is reached. */
    push(event: T) {
        this.buffer.push(event)

        if (this.buffer.length >= this.config.maxBufferSize) {
            this.flush()
        } else {
            this.scheduleFlush()
        }
    }

    /** Flush all buffered events via the async transport immediately. */
    flush() {
        if (this.buffer.length === 0) return

        const events = this.buffer
        this.buffer = []
        this.clearTimer()

        this.config.sendBatch(events).catch((error) => {
            if (this.config.onError) {
                this.config.onError(error)
            }
        })
    }

    /** Number of buffered events. */
    get size() {
        return this.buffer.length
    }

    // -----------------------------------------------------------------------

    private scheduleFlush() {
        if (this.timer) return
        this.timer = setTimeout(() => {
            this.timer = null
            this.flush()
        }, this.config.flushIntervalMs)
    }

    private clearTimer() {
        if (this.timer) {
            clearTimeout(this.timer)
            this.timer = null
        }
    }

    private handleUnload = () => {
        if (this.buffer.length === 0) return

        const events = this.buffer
        this.buffer = []
        this.clearTimer()

        if (this.config.sendBeaconBatch) {
            try {
                this.config.sendBeaconBatch(events)
            } catch {
                // If sendBeacon fails, events are lost — acceptable trade-off
            }
        }
    }

    private registerUnloadHandlers() {
        window.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') this.handleUnload()
        })
        window.addEventListener('pagehide', this.handleUnload)
    }
}
